import React, { useMemo } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Platform } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { Ionicons } from '@expo/vector-icons';
import theme from '@/config/theme';
import { isIOS } from '@/utils/platform';

interface TrialBannerProps {
  expiresAt?: string | null;
  onClose: () => void;
  onUpgrade: () => void;
}

const formatDate = (value?: string | null) => {
  if (!value) {
    return 'em breve';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return 'em breve';
  }

  try {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: 'long',
    }).format(date);
  } catch {
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
    });
  }
};

const getDaysLeft = (value?: string | null) => {
  if (!value) return null;
  const target = new Date(value);
  if (Number.isNaN(target.getTime())) return null;
  const diffMs = target.getTime() - Date.now();
  const days = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  return Math.max(days, 0);
};

const TrialBanner: React.FC<TrialBannerProps> = ({ expiresAt, onClose, onUpgrade: _onUpgrade }) => {
  if (isIOS) {
    return null;
  }

  const formattedDate = useMemo(() => formatDate(expiresAt), [expiresAt]);
  const daysLeft = useMemo(() => getDaysLeft(expiresAt), [expiresAt]);

  const headline =
    daysLeft && daysLeft > 0
      ? `Teste gratuito ativo — faltam ${daysLeft} dia${daysLeft > 1 ? 's' : ''}`
      : 'Teste gratuito ativo';

  return (
    <LinearGradient colors={theme.colors.primary.gradient} style={styles.container}>
      <TouchableOpacity style={styles.closeButton} onPress={onClose} accessibilityRole="button">
        <Ionicons name="close" size={18} color={theme.colors.primary.contrast} />
      </TouchableOpacity>

      <View style={styles.content}>
        <Text style={styles.title}>{headline}</Text>
        <Text style={styles.subtitle}>
          Aproveite todos os recursos premium durante o período de teste. Expira em {formattedDate}.
        </Text>

        {/* CTA removida conforme solicitação: foco apenas no aviso do trial */}
      </View>
    </LinearGradient>
  );
};

const styles = StyleSheet.create({
  container: {
    borderRadius: theme.borderRadius.lg,
    marginHorizontal: theme.spacing.md,
    marginTop: theme.spacing.md,
    padding: theme.spacing.lg,
    ...Platform.select({
      web: {
        boxShadow: '0 12px 24px rgba(79, 115, 225, 0.35)',
      },
      ios: {
        shadowColor: theme.colors.primary.dark,
        shadowOffset: { width: 0, height: 10 },
        shadowOpacity: 0.35,
        shadowRadius: 12,
      },
      android: {
        elevation: 6,
      },
    }),
  },
  closeButton: {
    position: 'absolute',
    top: theme.spacing.sm,
    right: theme.spacing.sm,
    width: 32,
    height: 32,
    borderRadius: theme.borderRadius.circular,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
  },
  content: {
    gap: theme.spacing.sm,
    paddingRight: theme.spacing.xl,
  },
  title: {
    color: theme.colors.primary.contrast,
    fontSize: theme.fontSizes.lg,
    fontWeight: theme.fontWeights.bold as any,
  },
  subtitle: {
    color: `${theme.colors.primary.contrast}CC`,
    fontSize: theme.fontSizes.md,
    lineHeight: theme.fontSizes.lg,
  },
});

export default TrialBanner;
